import { AfterViewInit, Component, HostListener, OnInit } from '@angular/core';
import {
  RouteConfigLoadEnd,
  RouteConfigLoadStart,
  Router,
} from '@angular/router';
import { LocalizationService } from './core/services/localization.service';
import { ZoidAppService } from '@core/services/zoid-app.service';
import { CustomIconsRegisterService } from '@core/services/custom-icon-register.service';
import { LiveChatStateService } from './modules/live-chat/services/live-chat-state.service';
import { LoggerService } from './core/services/logger.service';
import { LogEvent } from './core/enums/log-event';
import { StatusOptions } from './modules/live-chat/interfaces/phone-status.interface';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from 'asksuite-citrus';
import { TokenRefreshSchedulerService } from './core/services/token-refresh-scheduler.service';
import { VersionCheckService } from './core/services/version-check.service';
import { PhoneService } from './modules/live-chat/services/phone.service';
import { ConnectionExpiredService } from './core/services/connection-expired.service';
import { ChildAppMessageService } from './core/services/child-app-message.service';
import { UserInactivityService } from './core/services/user-inactivity.service';
import { take } from 'rxjs';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements AfterViewInit, OnInit {
  loading = false;
  sessionExpired = false;
  connectionExpired = false;
  showUpdateBanner = false;
  releaseMessage = '';

  constructor(
    private router: Router,
    private localizationService: LocalizationService,
    private zoidAppService: ZoidAppService,
    private customIconRegister: CustomIconsRegisterService,
    private liveChatStateService: LiveChatStateService,
    private loggerService: LoggerService,
    private translateService: TranslateService,
    private toastService: ToastService,
    private tokenRefreshScheduler: TokenRefreshSchedulerService,
    private versionCheckService: VersionCheckService,
    private phoneService: PhoneService,
    private connectionExpiredService: ConnectionExpiredService,
    private childAppMessageService: ChildAppMessageService,
    private userInactivityService: UserInactivityService
  ) {
    this.handleModuleLoading();
    this.localizationService.prepare();
    this.customIconRegister.register();
  }

  ngOnInit(): void {
    this.tokenRefreshScheduler.sessionExpired$.subscribe((expired) => {
      this.sessionExpired = expired;
    });
    this.tokenRefreshScheduler.start();

    this.childAppMessageService.start();

    this.connectionExpiredService.connectionExpired$.subscribe((expired) => {
      this.connectionExpired = expired;
    });

    this.versionCheckService.updateAvailable$.subscribe((available) => {
      this.showUpdateBanner = available;
    });
    this.versionCheckService.releaseMessage$.subscribe((message) => {
      this.releaseMessage = message;
    });
    this.versionCheckService.startPolling();

    this.userInactivityService.idle$.subscribe();
  }

  onApplyUpdate(): void {
    this.versionCheckService.applyUpdate();
  }

  onDismissUpdate(): void {
    this.versionCheckService.dismiss();
  }

  @HostListener('window:beforeunload', ['$event'])
  beforeUnloadHandler(event: BeforeUnloadEvent) {
    if (this.isPhoneOnline()) {
      this.loggerService.log(LogEvent.LIVECHAT_PHONE_ONLINE_BEFORE_UNLOAD);
      event.preventDefault();
    }
  }

  @HostListener('window:unload')
  unloadHandler() {
    if (this.isPhoneOnline()) {
      this.loggerService.logWithBeacon(LogEvent.LIVECHAT_PHONE_ONLINE_UNLOAD);
    }
  }

  ngAfterViewInit(): void {
    this.phoneService.checkShowPhoneApp()
      .pipe(take(1))
      .subscribe((canUsePhone) => {
        this.zoidAppService.preloadApps({
          name: 'phoneApp',
          loadOptions: {
            preload: canUsePhone,
            autoClose: false,
          },
        });
      });
  }

  handleModuleLoading() {
    this.router.events.subscribe((event) => {
      if (event instanceof RouteConfigLoadStart) {
        this.loading = true;
      } else if (event instanceof RouteConfigLoadEnd) {
        this.loading = false;
      }
    });
  }

  private isPhoneOnline(): boolean {
    const phoneStatus = this.liveChatStateService.currentPhoneAppStatus;
    return phoneStatus?.description === StatusOptions.ONLINE;
  }
}
